import { utils, writeFile } from "@e965/xlsx";
import { UserRole, type GetBuildingHistoryResponseBody, type HistoryEntry } from "@eisox/api/core-api";
import dayjs, { type ConfigType } from "@eisox/dayjs";

import { HISTORY_LOOKBACK_DAYS } from "~/constants/history.constant";
import { m } from "~/paraglide/messages";

/** Sampling interval assumed for valves and boiler rooms (minutes). */
export const HISTORY_SAMPLE_INTERVAL_MINUTES = 5;

/** Points per 24 h at {@link HISTORY_SAMPLE_INTERVAL_MINUTES} sampling — fits one paginated request. */
export const HISTORY_DAY_POINT_LIMIT = (24 * 60) / HISTORY_SAMPLE_INTERVAL_MINUTES;

/** Backend default (100) only covers ~8 h; derive limit from the requested window instead. */
export const getHistoryLimitForRange = (startDate: string, endDate: string): number => {
  const minutes = Math.max(HISTORY_SAMPLE_INTERVAL_MINUTES, dayjs(endDate).diff(dayjs(startDate), "minute"));
  return Math.ceil(minutes / HISTORY_SAMPLE_INTERVAL_MINUTES);
};

type HistoryPage = Pick<GetBuildingHistoryResponseBody, "data" | "nextCursor">;

/** Fetches every page until `nextCursor` is absent. */
export const collectHistoryPages = async (
  fetchPage: (cursor?: string) => Promise<HistoryPage>,
): Promise<HistoryEntry[]> => {
  const entries: HistoryEntry[] = [];
  let cursor: string | undefined;

  do {
    const page = await fetchPage(cursor);
    entries.push(...page.data);
    cursor = page.nextCursor;
  } while (cursor);

  return entries;
};

export const STATE_HISTORY_DATES_STALE_TIME_MS = 10 * 60 * 1000;

export const LIVE_POLL_INTERVAL_MS = 10_000;

/** User inactivity before Live polling pauses. */
export const LIVE_IDLE_TIMEOUT_MS = 60_000;

/** Freshest live sample older than this ⇒ realtime is unavailable. */
export const REALTIME_STALE_THRESHOLD_MS = 9 * 60 * 1000;

export const isLiveSampleStale = (sampleDate: ConfigType | undefined): boolean =>
  !sampleDate || dayjs().diff(sampleDate) > REALTIME_STALE_THRESHOLD_MS;

/** Consecutive polls with an unchanged report before the live feed counts as frozen. */
export const REALTIME_FROZEN_FETCH_COUNT = 4;

export const buildStateHistoryDayWindow = (day: ConfigType): { startDate: string; endDate: string } => ({
  startDate: dayjs(day).startOf("day").toISOString(),
  endDate: dayjs(day).endOf("day").toISOString(),
});

export const getStateHistoryDays = (now: Date): string[] =>
  Array.from({ length: HISTORY_LOOKBACK_DAYS }, (_, i) => dayjs(now).subtract(i, "day").format("YYYY-MM-DD"));

export const findClosestDate = (dates: string[], target: string): string | undefined => {
  const targetMs = dayjs(target).valueOf();
  return dates.reduce<string | undefined>(
    (closest, d) =>
      closest === undefined || Math.abs(dayjs(d).valueOf() - targetMs) < Math.abs(dayjs(closest).valueOf() - targetMs)
        ? d
        : closest,
    undefined,
  );
};

export enum ValvePoints {
  TEMPERATURE = "temperature",
  CORRECTED_TEMP = "correctedTemp",
  INSTRUCTION_TEMP = "instructionTemp",
  EXTERNAL_TEMP = "externalTemp",
  CORRECTION = "correction",
  HYGROMETRY = "hygrometry",
  CLOSING = "closing",
  HUMIDITY = "humidity",
  MAX_OPENING = "maxOpening",
  HSLOPE_MIN_C = "hslopeMinC",
  PRESSURE = "pressure",
  LIGHT = "light",
  GAZ_RAW = "gazRaw",
  INDOOR_AIR_QUALITY = "indoorAirQuality",
  SOUND_COUNTER = "soundCounter",
  RSSI = "rssi",
  LQI = "lqi",
  CO2 = "co2",
  BVOC = "bvoc",
  TRIG_CUR = "trigCur",
}

export const MAX_VALVE_HISTORY_SELECTIONS = 6;

export const DEFAULT_POINTS = [
  ValvePoints.TEMPERATURE,
  ValvePoints.CLOSING,
  ValvePoints.INSTRUCTION_TEMP,
  ValvePoints.CORRECTED_TEMP,
];

export type ChartDataPoint = Record<string, number | null> & { date: number };

export const getDateOptions = (startOfDay = true, length = 30): { value: string; name: string }[] =>
  Array.from({ length: length }).map((_, i) => {
    let date = dayjs().subtract(i, "day");
    date = startOfDay ? date.startOf("day") : date.endOf("day");
    return { value: date.toISOString(), name: date.format("ddd DD MMM") };
  });

const toDisplayedValue = (point: string, value: number, role?: UserRole): number =>
  point === ValvePoints.CLOSING
    ? 100 - (role === UserRole.INSTALLER ? value : Math.min(Math.max(value, 0), 100))
    : value;

export const transformHistoryData = (
  data: HistoryEntry[] | undefined,
  points: string[],
  role?: UserRole,
): ChartDataPoint[] => {
  if (!data?.length || !points.length) return [];

  const byTimestamp = new Map<number, ChartDataPoint>();

  for (const item of data) {
    if (!points.includes(item.point)) continue;
    if (typeof item.value !== "number") continue;

    // Sub-minute jitter (often 1 ms) would otherwise create a distinct x-axis point per sample.
    const timestamp = dayjs(item.timestamp).startOf("minute").valueOf();
    if (!Number.isFinite(timestamp)) continue;

    let row = byTimestamp.get(timestamp);
    if (!row) {
      row = { date: timestamp };
      byTimestamp.set(timestamp, row);
    }

    row[item.point] = parseFloat(toDisplayedValue(item.point, item.value, role).toFixed(1));
  }

  return Array.from(byTimestamp.values()).sort((a, b) => a.date - b.date);
};

export const formatUnit = (key: string, isAirQualityV2 = false): string => {
  switch (key) {
    case ValvePoints.TEMPERATURE:
    case ValvePoints.CORRECTED_TEMP:
    case ValvePoints.INSTRUCTION_TEMP:
    case ValvePoints.EXTERNAL_TEMP:
    case ValvePoints.CORRECTION:
      return "°C";
    case ValvePoints.HYGROMETRY:
      return "g/m3";
    case ValvePoints.CLOSING:
    case ValvePoints.HUMIDITY:
    case ValvePoints.MAX_OPENING:
      return "%";
    case ValvePoints.HSLOPE_MIN_C:
      return "min/°C";
    case ValvePoints.PRESSURE:
      return "hPa";
    case ValvePoints.LIGHT:
      return "Lux";
    case ValvePoints.GAZ_RAW:
      return "Ohms";
    case ValvePoints.INDOOR_AIR_QUALITY:
      return isAirQualityV2 ? "Index (1-5)" : "Index (0-500)";
    case ValvePoints.SOUND_COUNTER:
      return "Compteur";
    case ValvePoints.RSSI:
      return "dBm";
    case ValvePoints.LQI:
      return "Index 0-255";
    case ValvePoints.CO2:
    case ValvePoints.BVOC:
      return "ppm";
    default:
      return "";
  }
};

export const getUnitAxesMapping = (keys: string[], isAirQualityV2 = false): Record<string, string[]> => {
  const unitAxesMapping: Record<string, string[]> = {};

  keys.forEach(key => {
    const unit = formatUnit(key, isAirQualityV2);
    if (!unitAxesMapping[unit]) {
      unitAxesMapping[unit] = [];
    }
    unitAxesMapping[unit].push(key);
  });

  return unitAxesMapping;
};

export const appendUnitsToLabels = (
  labels: Record<string, string>,
  unitAxesMapping: Record<string, string[]>,
): Record<string, string> => {
  const unitBySeriesKey = new Map<string, string>();

  for (const [unit, seriesKeys] of Object.entries(unitAxesMapping)) {
    for (const seriesKey of seriesKeys) {
      unitBySeriesKey.set(seriesKey, unit);
    }
  }

  return Object.fromEntries(
    Object.entries(labels).map(([key, label]) => {
      const unit = unitBySeriesKey.get(key);
      return [key, unit ? `${label} (${unit})` : label];
    }),
  );
};

export const getValvePointLabel = (point: string): string => {
  switch (point) {
    case ValvePoints.TEMPERATURE:
      return m.temperature();
    case ValvePoints.CORRECTED_TEMP:
      return m.corrected_temperature();
    case ValvePoints.INSTRUCTION_TEMP:
      return m.planning();
    case ValvePoints.EXTERNAL_TEMP:
      return m.external_temperature();
    case ValvePoints.CORRECTION:
      return m.correction();
    case ValvePoints.HYGROMETRY:
      return m.hygrometry();
    case ValvePoints.CLOSING:
      return m.opening();
    case ValvePoints.HUMIDITY:
      return m.humidity();
    case ValvePoints.MAX_OPENING:
      return m.max_opening();
    case ValvePoints.HSLOPE_MIN_C:
      return m.hslope();
    case ValvePoints.PRESSURE:
      return m.pressure();
    case ValvePoints.LIGHT:
      return m.light();
    case ValvePoints.GAZ_RAW:
      return m.gas();
    case ValvePoints.INDOOR_AIR_QUALITY:
      return m.air_quality();
    case ValvePoints.SOUND_COUNTER:
      return m.sound_counter();
    case ValvePoints.RSSI:
      return "RSSI";
    case ValvePoints.LQI:
      return "LQI";
    case ValvePoints.CO2:
      return "CO2";
    case ValvePoints.BVOC:
      return "b-VOC";
    case ValvePoints.TRIG_CUR:
      return "trigCur";
    default:
      return point;
  }
};

export const getValvePointOptions = (isAirQualityV2 = false): { value: string; name: string }[] =>
  Object.values(ValvePoints).map(point => {
    const label = getValvePointLabel(point);
    const unit = formatUnit(point, isAirQualityV2);
    return { value: point, name: unit ? `${label} (${unit})` : label };
  });

export const exportData = (data: Record<string, string | number>[], filename: string, format: "csv" | "xlsx") => {
  const worksheet = utils.json_to_sheet(data);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, filename);
  writeFile(workbook, `${filename}.${format}`);
};

export const formatHistoryTooltipDate = (timestamp: number): string => {
  const date = dayjs(timestamp);
  const time = date.format("LT");

  if (date.isToday()) {
    return m.today_at({ time });
  }

  if (date.isYesterday()) {
    return m.yesterday_at({ time });
  }

  return m.date_at({ date: date.format("L"), time });
};
